import React, { useRef, useEffect, useState } from "react";
import { Card } from "antd";
import { RiUser3Fill } from "react-icons/ri";
import { SYMBOL_TYPES } from "../../FloorPlanModal/utils/symbolTypes";
import { getStationColor } from "../../FloorPlanModal/utils/stationUtils";
import "./CanvasLegend.css";

const CanvasLegend = ({ placedSymbols, baseStations, equipment = [] }) => {
  const contentRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const personnelStatuses = [
    { status: "active", label: "在線", color: "#52c41a" },
    { status: "offline", label: "離線", color: "#8c8c8c" },
    { status: "warning", label: "注意", color: "#faad14" },
    { status: "sos", label: "緊急", color: "#ff4d4f" },
  ];

  const stationCategories = [
    { category: "定位基站", label: "定位基站" },
    { category: "防護區", label: "防護區" },
    { category: "其他基站", label: "其他基站" },
  ];

  const allSymbolTypes = Object.values(SYMBOL_TYPES);

  const uniqueStationCategories = stationCategories.filter((cat) =>
    baseStations.some((station) => station.category === cat.category),
  );

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const { scrollHeight, clientHeight } = contentRef.current;
        setIsOverflowing(scrollHeight > clientHeight);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [baseStations]);

  return (
    <Card
      size="small"
      bordered
      className="canvas-legend-card"
      style={{ height: 460, display: "flex", flexDirection: "column" }}
      bodyStyle={{
        padding: 8,
        flex: 1,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        ref={contentRef}
        className={`legend-content ${isOverflowing ? "legend-marquee" : ""}`}
      >
        <div className="legend-section">
          <div className="legend-section-title">人員狀態</div>
          <div className="legend-items">
            {personnelStatuses.map(({ status, label, color }) => (
              <div key={status} className="legend-item">
                <div className="legend-icon-wrapper">
                  <RiUser3Fill size={16} style={{ color }} />
                </div>
                <span className="legend-label">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {uniqueStationCategories.length > 0 && (
          <div className="legend-section">
            <div className="legend-section-title">基站類型</div>
            <div className="legend-items">
              {uniqueStationCategories.map(({ category, label }) => {
                const stationColor = getStationColor(category);
                return (
                  <div key={category} className="legend-item">
                    <div className="legend-icon-wrapper">
                      <div
                        className="station-circle"
                        style={{ backgroundColor: stationColor.primary }}
                      />
                    </div>
                    <span className="legend-label">{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="legend-section">
          <div className="legend-section-title">物件圖示</div>
          <div className="legend-items-grid">
            {allSymbolTypes.map((symbolType) => {
              const IconComponent = symbolType.icon;
              return (
                <div key={symbolType.id} className="legend-item">
                  <div className="legend-icon-wrapper">
                    <IconComponent
                      size={16}
                      style={{ color: symbolType.color }}
                    />
                  </div>
                  <span className="legend-label">{symbolType.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        {equipment.length > 0 && (
          <div className="legend-section">
            <div className="legend-section-title">機具標記</div>
            <div className="legend-items">
              <div className="legend-item">
                <div className="legend-icon-wrapper">
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "5px solid transparent",
                      borderRight: "5px solid transparent",
                      borderBottom: "9px solid #722ed1",
                    }}
                  />
                </div>
                <span className="legend-label">車頭</span>
              </div>
              <div className="legend-item">
                <div className="legend-icon-wrapper">
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 1,
                      backgroundColor: "#722ed1",
                    }}
                  />
                </div>
                <span className="legend-label">車尾</span>
              </div>
              {/* Matches what the canvas now draws for a degraded machine: the
                  machine's own icon in grey with a dashed border, not the
                  anonymous grey dot this used to show. */}
              <div className="legend-item">
                <div className="legend-icon-wrapper">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 3,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      border: "2px dashed #8c8c8c",
                    }}
                  />
                </div>
                <span className="legend-label">
                  定位異常（多定位器僅剩單點）
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default CanvasLegend;
